import {
  AlertTriangle,
  Boxes,
  Building2,
  CheckCircle2,
  ClipboardList,
  Clock3,
  LogOut,
  MapPin,
  PackageCheck,
  RefreshCcw,
  Search,
  ShieldCheck,
  ShoppingCart,
  Truck,
  WalletCards,
} from "lucide-react";
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  AdminApiError,
  adminApiBaseUrl,
  assignSampleRequestRider,
  createAdminSession,
  loadAdminDataset,
  updateSampleCampaignStatus,
  updateSampleRequestPayout,
  updateSampleRequestStatus,
} from "./api";
import {
  getCampaignStatusLabel,
  getPayoutStatusLabel,
  getRequestStatusClass,
  getRequestStatusLabel,
  getRequestStatusMeta,
  QUICK_STATUS_ACTIONS,
  REQUEST_STATUS_OPTIONS,
} from "./status";
import type {
  AdminDataset,
  AdminRecord,
  DeliveryFilters,
  LoadState,
  RiderSummary,
  SampleRequest,
  SectionKey,
} from "./types";

const TOKEN_STORAGE_KEY = "kithub.adminWebV2.token";

const EMPTY_DATASET: AdminDataset = {
  requests: [],
  riders: [],
  campaigns: [],
  products: [],
  suppliers: [],
  orders: [],
  orderClaims: [],
};

const INITIAL_FILTERS: DeliveryFilters = {
  status: "all",
  campaignId: "",
  rider: "",
  region: "",
  keyword: "",
};

const NAV_ITEMS: Array<{
  key: SectionKey;
  label: string;
  description: string;
  icon: typeof Truck;
}> = [
  {
    key: "delivery",
    label: "배송관리",
    description: "신청부터 정산까지",
    icon: Truck,
  },
  {
    key: "campaigns",
    label: "캠페인",
    description: "전체 캠페인 운영",
    icon: ClipboardList,
  },
  {
    key: "products",
    label: "상품",
    description: "상품 승인과 상태",
    icon: Boxes,
  },
  {
    key: "suppliers",
    label: "공급자",
    description: "브랜드 계정 관리",
    icon: Building2,
  },
  {
    key: "commerce",
    label: "주문/클레임",
    description: "커머스 이슈",
    icon: ShoppingCart,
  },
];

const DONE_STATUSES = new Set([
  "delivered",
  "received_confirmed",
  "survey_completed",
  "fulfilled",
]);

const PREPARING_STATUSES = new Set(["preparing", "rider_assigned"]);

export function App() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY));
  const [section, setSection] = useState<SectionKey>("delivery");
  const [dataset, setDataset] = useState<AdminDataset>(EMPTY_DATASET);
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [message, setMessage] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState<string>("");
  const [busyId, setBusyId] = useState("");
  const [filters, setFilters] = useState<DeliveryFilters>(INITIAL_FILTERS);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
    setDataset(EMPTY_DATASET);
    setSelectedRequestId("");
    setLoadState("idle");
  }, []);

  const handleError = useCallback(
    (error: unknown) => {
      if (error instanceof AdminApiError && error.status === 401) {
        setMessage("관리자 세션이 만료되었습니다. 다시 로그인해 주세요.");
        logout();
        return;
      }
      setMessage(error instanceof Error ? error.message : String(error));
    },
    [logout],
  );

  const reload = useCallback(
    async (silent = false) => {
      if (!token) return;
      if (!silent) setLoadState("loading");
      try {
        const next = await loadAdminDataset(token);
        setDataset(next);
        setLoadState("ready");
        if (!selectedRequestId && next.requests[0]) {
          setSelectedRequestId(next.requests[0].id);
        }
      } catch (error) {
        setLoadState("error");
        handleError(error);
      }
    },
    [handleError, selectedRequestId, token],
  );

  useEffect(() => {
    if (token) void reload();
  }, [reload, token]);

  const selectedRequest = useMemo(
    () =>
      dataset.requests.find((request) => request.id === selectedRequestId) ??
      dataset.requests[0] ??
      null,
    [dataset.requests, selectedRequestId],
  );

  async function runRequestAction(
    request: SampleRequest,
    action: () => Promise<unknown>,
    successMessage: string,
  ) {
    if (!token) return;
    setBusyId(request.id);
    setMessage("");
    try {
      await action();
      await reload(true);
      setMessage(successMessage);
    } catch (error) {
      handleError(error);
    } finally {
      setBusyId("");
    }
  }

  async function handleLogin(password: string) {
    setMessage("");
    const session = await createAdminSession(password);
    localStorage.setItem(TOKEN_STORAGE_KEY, session.token);
    setToken(session.token);
  }

  async function handleRequestStatus(request: SampleRequest, status: string) {
    await runRequestAction(
      request,
      () => updateSampleRequestStatus(token!, request.id, status),
      `${requestLabel(request)} 상태를 ${getRequestStatusLabel(status)}(으)로 변경했습니다.`,
    );
  }

  async function handleAssignRider(request: SampleRequest, rider?: RiderSummary | null) {
    await runRequestAction(
      request,
      () => assignSampleRequestRider(token!, request.id, rider),
      `${requestLabel(request)} 배송준비 처리를 완료했습니다.`,
    );
  }

  async function handlePayout(
    request: SampleRequest,
    payoutStatus: string,
    payoutAmount?: number,
  ) {
    await runRequestAction(
      request,
      () => updateSampleRequestPayout(token!, request.id, payoutStatus, payoutAmount),
      `${requestLabel(request)} 정산 정보를 저장했습니다.`,
    );
  }

  async function handleCampaignStatus(campaign: AdminRecord, status: string) {
    if (!token || !campaign.id) return;
    setBusyId(campaign.id);
    setMessage("");
    try {
      await updateSampleCampaignStatus(token, campaign.id, status);
      await reload(true);
      setMessage(`${recordTitle(campaign)} 캠페인 상태를 ${getCampaignStatusLabel(status)}(으)로 변경했습니다.`);
    } catch (error) {
      handleError(error);
    } finally {
      setBusyId("");
    }
  }

  if (!token) {
    return <LoginScreen apiBaseUrl={adminApiBaseUrl} message={message} onLogin={handleLogin} />;
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-block">
          <div className="brand-mark">KH</div>
          <div>
            <strong>관리자웹 v2</strong>
            <span>배송 중심 운영 콘솔</span>
          </div>
        </div>

        <nav className="nav-list" aria-label="관리자 메뉴">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <button
                className={`nav-item ${section === item.key ? "active" : ""}`}
                key={item.key}
                onClick={() => setSection(item.key)}
                type="button"
              >
                <Icon size={20} />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <span>v{__APP_VERSION__}</span>
          <button className="ghost-button" onClick={logout} type="button">
            <LogOut size={16} />
            로그아웃
          </button>
        </div>
      </aside>

      <main className="main-area">
        <header className="topbar">
          <div>
            <p className="eyebrow">KitHub Admin Operations</p>
            <h1>{NAV_ITEMS.find((item) => item.key === section)?.label}</h1>
            <span className="topbar-subtitle">
              모든 캠페인을 한 화면에서 보고 배송 병목을 먼저 처리합니다.
            </span>
          </div>
          <div className="topbar-actions">
            <button
              className="secondary-button"
              disabled={loadState === "loading"}
              onClick={() => void reload()}
              type="button"
            >
              <RefreshCcw size={16} />
              새로고침
            </button>
          </div>
        </header>

        {message ? <div className="message-bar">{message}</div> : null}

        {loadState === "loading" && dataset.requests.length === 0 ? (
          <div className="loading-panel">관리자 데이터를 불러오는 중입니다.</div>
        ) : null}

        {section === "delivery" ? (
          <DeliveryWorkspace
            busyId={busyId}
            dataset={dataset}
            filters={filters}
            onAssignRider={handleAssignRider}
            onPayout={handlePayout}
            onSelectRequest={setSelectedRequestId}
            onStatusChange={handleRequestStatus}
            selectedRequest={selectedRequest}
            setFilters={setFilters}
          />
        ) : null}

        {section === "campaigns" ? (
          <CampaignWorkspace
            busyId={busyId}
            campaigns={dataset.campaigns}
            onStatusChange={handleCampaignStatus}
          />
        ) : null}

        {section === "products" ? (
          <RecordWorkspace
            description="상품 승인 상태와 노출 상태를 빠르게 확인합니다."
            icon={<Boxes size={20} />}
            records={dataset.products}
            title="상품 관리"
            columns={[
              ["상품", ["name", "productName", "title"]],
              ["브랜드", ["brandName", "brand_name", "supplierCompanyName"]],
              ["상태", ["status"]],
              ["등록일", ["createdAt", "created_at"]],
            ]}
          />
        ) : null}

        {section === "suppliers" ? (
          <RecordWorkspace
            description="공급자 계정, 회사명, 승인 상태를 한 번에 봅니다."
            icon={<Building2 size={20} />}
            records={dataset.suppliers}
            title="공급자 관리"
            columns={[
              ["회사", ["companyName", "company_name", "name"]],
              ["담당자", ["contactName", "contact_name"]],
              ["상태", ["status"]],
              ["가입일", ["createdAt", "created_at"]],
            ]}
          />
        ) : null}

        {section === "commerce" ? (
          <CommerceWorkspace orders={dataset.orders} claims={dataset.orderClaims} />
        ) : null}
      </main>
    </div>
  );
}

function LoginScreen({
  apiBaseUrl,
  message,
  onLogin,
}: {
  apiBaseUrl: string;
  message: string;
  onLogin: (password: string) => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onLogin(password);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : String(loginError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-card">
        <div className="login-hero">
          <div className="brand-mark large">KH</div>
          <p className="eyebrow">Admin Web v2</p>
          <h1>배송을 먼저 보는 관리자 콘솔</h1>
          <p>
            신청완료, 배송준비, 배송중, 배송완료, 정산대기까지 한 화면에서
            이어서 처리합니다.
          </p>
        </div>
        <form className="login-form" onSubmit={submit}>
          <label>
            관리자 비밀번호
            <input
              autoComplete="current-password"
              autoFocus
              onChange={(event) => setPassword(event.target.value)}
              placeholder="비밀번호 입력"
              type="password"
              value={password}
            />
          </label>
          <button className="primary-button" disabled={busy || !password.trim()} type="submit">
            <ShieldCheck size={18} />
            {busy ? "확인 중" : "관리자 로그인"}
          </button>
          <small>API: {apiBaseUrl}</small>
          {error || message ? <div className="error-text">{error || message}</div> : null}
        </form>
      </section>
    </main>
  );
}

function DeliveryWorkspace({
  busyId,
  dataset,
  filters,
  onAssignRider,
  onPayout,
  onSelectRequest,
  onStatusChange,
  selectedRequest,
  setFilters,
}: {
  busyId: string;
  dataset: AdminDataset;
  filters: DeliveryFilters;
  onAssignRider: (request: SampleRequest, rider?: RiderSummary | null) => Promise<void>;
  onPayout: (request: SampleRequest, payoutStatus: string, payoutAmount?: number) => Promise<void>;
  onSelectRequest: (id: string) => void;
  onStatusChange: (request: SampleRequest, status: string) => Promise<void>;
  selectedRequest: SampleRequest | null;
  setFilters: Dispatch<SetStateAction<DeliveryFilters>>;
}) {
  const filtered = useMemo(
    () => filterRequests(dataset.requests, filters),
    [dataset.requests, filters],
  );
  const stats = useMemo(() => buildDeliveryStats(dataset.requests), [dataset.requests]);
  const campaignOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const request of dataset.requests) {
      if (request.sampleCampaignId) {
        map.set(request.sampleCampaignId, request.sampleCampaignTitle || request.productName || request.sampleCampaignId);
      }
    }
    return Array.from(map, ([id, title]) => ({ id, title })).sort((a, b) =>
      a.title.localeCompare(b.title, "ko"),
    );
  }, [dataset.requests]);

  return (
    <div className="delivery-grid">
      <section className="content-stack">
        <div className="metric-grid">
          <MetricCard icon={<PackageCheck size={22} />} label="신청완료" value={stats.requested} tone="blue" />
          <MetricCard icon={<Clock3 size={22} />} label="배송준비" value={stats.preparing} tone="amber" />
          <MetricCard icon={<Truck size={22} />} label="배송중" value={stats.shipping} tone="purple" />
          <MetricCard icon={<CheckCircle2 size={22} />} label="완료/수령" value={stats.done} tone="green" />
          <MetricCard icon={<AlertTriangle size={22} />} label="문제/취소" value={stats.issue} tone="red" />
          <MetricCard icon={<WalletCards size={22} />} label="정산대기" value={stats.payoutPending} tone="gray" />
        </div>

        <section className="panel priority-panel">
          <div>
            <p className="eyebrow">Delivery Priority</p>
            <h2>오늘 처리할 배송</h2>
            <p>
              신청완료 {stats.requested}건, 배송준비 {stats.preparing}건,
              배송중 {stats.shipping}건입니다. 배송준비 버튼은 공용 라이더 또는
              선택 라이더로 바로 배정합니다.
            </p>
          </div>
          <div className="priority-number">{stats.requested + stats.preparing + stats.shipping}</div>
        </section>

        <DeliveryFiltersPanel
          campaignOptions={campaignOptions}
          filters={filters}
          riders={dataset.riders}
          setFilters={setFilters}
        />

        <DeliveryBoard
          busyId={busyId}
          requests={filtered}
          onAssignRider={onAssignRider}
          onSelectRequest={onSelectRequest}
          selectedRequestId={selectedRequest?.id ?? ""}
        />

        <DeliveryTable
          requests={filtered}
          onSelectRequest={onSelectRequest}
          selectedRequestId={selectedRequest?.id ?? ""}
        />
      </section>

      <RequestDetailPanel
        busy={busyId === selectedRequest?.id}
        request={selectedRequest}
        riders={dataset.riders}
        onAssignRider={onAssignRider}
        onPayout={onPayout}
        onStatusChange={onStatusChange}
      />
    </div>
  );
}

function DeliveryFiltersPanel({
  campaignOptions,
  filters,
  riders,
  setFilters,
}: {
  campaignOptions: Array<{ id: string; title: string }>;
  filters: DeliveryFilters;
  riders: RiderSummary[];
  setFilters: Dispatch<SetStateAction<DeliveryFilters>>;
}) {
  const update = (patch: Partial<DeliveryFilters>) =>
    setFilters((current) => ({ ...current, ...patch }));

  return (
    <section className="panel filter-panel">
      <label className="search-field">
        <Search size={18} />
        <input
          onChange={(event) => update({ keyword: event.target.value })}
          placeholder="상품, 캠페인, 수령자, 전화, 주소 검색"
          value={filters.keyword}
        />
      </label>
      <select onChange={(event) => update({ status: event.target.value })} value={filters.status}>
        <option value="all">전체 상태</option>
        <option value="requested">신청완료</option>
        <option value="preparing_group">배송준비 전체</option>
        <option value="shipping">배송중</option>
        <option value="delivered_group">배송완료/수령</option>
        <option value="issue_group">문제/취소</option>
        <option value="payout_pending">정산대기</option>
        {REQUEST_STATUS_OPTIONS.map((status) => (
          <option key={status.value} value={status.value}>
            {status.label} ({status.value})
          </option>
        ))}
      </select>
      <select
        onChange={(event) => update({ campaignId: event.target.value })}
        value={filters.campaignId}
      >
        <option value="">전체 캠페인</option>
        {campaignOptions.map((campaign) => (
          <option key={campaign.id} value={campaign.id}>
            {campaign.title}
          </option>
        ))}
      </select>
      <select onChange={(event) => update({ rider: event.target.value })} value={filters.rider}>
        <option value="">전체 라이더</option>
        <option value="unassigned">미배정</option>
        {riders.map((rider) => (
          <option key={rider.userId} value={rider.userId}>
            {rider.name} ({rider.activeDeliveryCount ?? 0})
          </option>
        ))}
      </select>
      <input
        onChange={(event) => update({ region: event.target.value })}
        placeholder="지역 검색"
        value={filters.region}
      />
      <button className="ghost-button" onClick={() => setFilters(INITIAL_FILTERS)} type="button">
        초기화
      </button>
    </section>
  );
}

function DeliveryBoard({
  busyId,
  requests,
  onAssignRider,
  onSelectRequest,
  selectedRequestId,
}: {
  busyId: string;
  requests: SampleRequest[];
  onAssignRider: (request: SampleRequest, rider?: RiderSummary | null) => Promise<void>;
  onSelectRequest: (id: string) => void;
  selectedRequestId: string;
}) {
  const columns = [
    {
      key: "requested",
      title: "신청완료",
      hint: "배송준비로 넘길 대상",
      items: requests.filter((item) => item.status === "requested"),
    },
    {
      key: "preparing",
      title: "배송준비",
      hint: "라이더 배정/상품준비",
      items: requests.filter((item) => PREPARING_STATUSES.has(item.status)),
    },
    {
      key: "shipping",
      title: "배송중",
      hint: "현장 처리 중",
      items: requests.filter((item) => item.status === "shipping"),
    },
    {
      key: "done",
      title: "배송완료",
      hint: "수령확인/설문 단계",
      items: requests.filter((item) => DONE_STATUSES.has(item.status)),
    },
    {
      key: "issue",
      title: "문제/취소",
      hint: "운영 확인 필요",
      items: requests.filter((item) => getRequestStatusMeta(item.status).group === "issue"),
    },
  ];

  return (
    <section className="board">
      {columns.map((column) => (
        <div className="board-column" key={column.key}>
          <div className="board-header">
            <div>
              <strong>{column.title}</strong>
              <span>{column.hint}</span>
            </div>
            <b>{column.items.length}</b>
          </div>
          <div className="board-list">
            {column.items.slice(0, 12).map((request) => (
              <button
                className={`request-card ${selectedRequestId === request.id ? "selected" : ""}`}
                key={request.id}
                onClick={() => onSelectRequest(request.id)}
                type="button"
              >
                <span className={getRequestStatusClass(request.status)}>
                  {getRequestStatusLabel(request.status)}
                </span>
                <strong>{requestLabel(request)}</strong>
                <small>{request.sampleCampaignTitle || request.brandName || "-"}</small>
                <span className="card-meta">
                  <MapPin size={14} />
                  {regionLabel(request)}
                </span>
                {request.status === "requested" || request.status === "preparing" ? (
                  <span
                    className="inline-action"
                    onClick={(event) => {
                      event.stopPropagation();
                      void onAssignRider(request);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {busyId === request.id ? "처리 중" : "배송준비"}
                  </span>
                ) : null}
              </button>
            ))}
            {column.items.length === 0 ? <div className="empty-column">대상 없음</div> : null}
          </div>
        </div>
      ))}
    </section>
  );
}

function DeliveryTable({
  requests,
  onSelectRequest,
  selectedRequestId,
}: {
  requests: SampleRequest[];
  onSelectRequest: (id: string) => void;
  selectedRequestId: string;
}) {
  return (
    <section className="panel table-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Dense Table</p>
          <h2>배송 요청 {requests.length}건</h2>
        </div>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>상태</th>
              <th>상품/캠페인</th>
              <th>수령자</th>
              <th>지역</th>
              <th>라이더</th>
              <th>신청일</th>
              <th>정산</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr
                className={selectedRequestId === request.id ? "selected-row" : ""}
                key={request.id}
                onClick={() => onSelectRequest(request.id)}
              >
                <td>
                  <span className={getRequestStatusClass(request.status)}>
                    {getRequestStatusLabel(request.status)}
                  </span>
                </td>
                <td>
                  <strong>{request.productName || "-"}</strong>
                  <small>{request.sampleCampaignTitle || request.brandName || "-"}</small>
                </td>
                <td>
                  <strong>{request.recipientNameSnapshot || "-"}</strong>
                  <small>{request.deliveryPhoneMaskedSnapshot || request.contactPhoneMaskedSnapshot || "-"}</small>
                </td>
                <td>{regionLabel(request)}</td>
                <td>{riderLabel(request)}</td>
                <td>{formatDate(request.requestedAt)}</td>
                <td>{getPayoutStatusLabel(request.payoutStatus)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RequestDetailPanel({
  busy,
  request,
  riders,
  onAssignRider,
  onPayout,
  onStatusChange,
}: {
  busy: boolean;
  request: SampleRequest | null;
  riders: RiderSummary[];
  onAssignRider: (request: SampleRequest, rider?: RiderSummary | null) => Promise<void>;
  onPayout: (request: SampleRequest, payoutStatus: string, payoutAmount?: number) => Promise<void>;
  onStatusChange: (request: SampleRequest, status: string) => Promise<void>;
}) {
  const [riderUserId, setRiderUserId] = useState("");
  const [payoutStatus, setPayoutStatus] = useState("pending");
  const [payoutAmount, setPayoutAmount] = useState("");

  useEffect(() => {
    setRiderUserId(request?.riderUserId ?? "");
    setPayoutStatus(request?.payoutStatus || "pending");
    setPayoutAmount(request?.payoutAmount ? String(request.payoutAmount) : "");
  }, [request]);

  if (!request) {
    return (
      <aside className="detail-panel">
        <div className="empty-detail">배송 요청을 선택하면 상세 정보가 표시됩니다.</div>
      </aside>
    );
  }

  const selectedRider = riders.find((rider) => rider.userId === riderUserId) ?? null;

  return (
    <aside className="detail-panel">
      <div className="detail-header">
        <span className={getRequestStatusClass(request.status)}>
          {getRequestStatusLabel(request.status)}
        </span>
        <h2>{requestLabel(request)}</h2>
        <p>{request.sampleCampaignTitle || request.brandName || "-"}</p>
      </div>

      <div className="detail-section">
        <h3>배송지</h3>
        <DetailRow label="수령자" value={request.recipientNameSnapshot} />
        <DetailRow
          label="연락처"
          value={request.deliveryPhoneMaskedSnapshot || request.contactPhoneMaskedSnapshot}
        />
        <DetailRow label="주소" value={fullAddress(request)} />
        <DetailRow label="지역" value={regionLabel(request)} />
        <DetailRow label="배송 메모" value={request.deliveryMemoSnapshot || request.deliveryMemo} />
      </div>

      <div className="detail-section">
        <h3>배송 처리</h3>
        <label className="field-label">
          라이더 선택
          <select onChange={(event) => setRiderUserId(event.target.value)} value={riderUserId}>
            <option value="">공용 배송 처리</option>
            {riders.map((rider) => (
              <option key={rider.userId} value={rider.userId}>
                {rider.name} / 현재 {rider.activeDeliveryCount ?? 0}건
              </option>
            ))}
          </select>
        </label>
        <button
          className="primary-button full"
          disabled={busy || !canAssignRider(request)}
          onClick={() => void onAssignRider(request, selectedRider)}
          type="button"
        >
          <Truck size={17} />
          {busy ? "처리 중" : "배송준비 처리"}
        </button>
        <div className="quick-actions">
          {QUICK_STATUS_ACTIONS.map((status) => (
            <button
              className="chip-button"
              disabled={busy || request.status === status}
              key={status}
              onClick={() => void onStatusChange(request, status)}
              type="button"
            >
              {getRequestStatusLabel(status)}
            </button>
          ))}
        </div>
      </div>

      <div className="detail-section">
        <h3>라이더/정산</h3>
        <DetailRow label="라이더" value={riderLabel(request)} />
        <DetailRow label="배정 시각" value={request.riderAssignedAt} />
        <DetailRow label="배송 완료" value={request.deliveryCompletedByRiderAt || request.deliveredAt} />
        <label className="field-label">
          정산 상태
          <select onChange={(event) => setPayoutStatus(event.target.value)} value={payoutStatus}>
            <option value="pending">정산대기</option>
            <option value="paid">입금완료</option>
          </select>
        </label>
        <label className="field-label">
          정산 금액
          <input
            inputMode="numeric"
            onChange={(event) => setPayoutAmount(event.target.value)}
            placeholder="예: 3500"
            value={payoutAmount}
          />
        </label>
        <button
          className="secondary-button full"
          disabled={busy}
          onClick={() =>
            void onPayout(request, payoutStatus, Number.parseFloat(payoutAmount) || undefined)
          }
          type="button"
        >
          <WalletCards size={17} />
          정산 저장
        </button>
      </div>

      <div className="detail-section">
        <h3>증빙/이슈</h3>
        <DetailRow label="배송 위치" value={locationLabel(request)} />
        <DetailRow label="미수령 사유" value={request.notReceivedReason} />
        <DetailRow label="수령 사진" value={request.receiptPhotoUrl ? "등록됨" : "-"} />
        {request.deliveryPhotoUrl ? (
          <a className="proof-link" href={request.deliveryPhotoUrl} rel="noreferrer" target="_blank">
            배송 사진 열기
          </a>
        ) : (
          <span className="muted">등록된 배송 사진 없음</span>
        )}
      </div>
    </aside>
  );
}

function CampaignWorkspace({
  busyId,
  campaigns,
  onStatusChange,
}: {
  busyId: string;
  campaigns: AdminRecord[];
  onStatusChange: (campaign: AdminRecord, status: string) => Promise<void>;
}) {
  return (
    <section className="panel workspace-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Campaign Control</p>
          <h2>전체 캠페인 {campaigns.length}개</h2>
          <p>승인, 진행, 일시중지, 완료 상태를 빠르게 확인합니다.</p>
        </div>
      </div>
      <div className="campaign-grid">
        {campaigns.map((campaign) => {
          const status = text(campaign.status, "unknown");
          return (
            <article className="campaign-card" key={campaign.id || recordTitle(campaign)}>
              <div className="campaign-card-top">
                <span className={status === "active" ? "status status-green" : "status status-gray"}>
                  {getCampaignStatusLabel(status)}
                </span>
                <strong>{recordTitle(campaign)}</strong>
              </div>
              <dl className="mini-dl">
                <div>
                  <dt>상품</dt>
                  <dd>{firstRecordText(campaign, ["productName", "product_name", "name"])}</dd>
                </div>
                <div>
                  <dt>수량</dt>
                  <dd>
                    {firstRecordText(campaign, ["requestedCount", "requested_count"], "0")} /
                    {firstRecordText(campaign, ["totalQuantity", "total_quantity"], "-")}
                  </dd>
                </div>
                <div>
                  <dt>지역</dt>
                  <dd>{firstRecordText(campaign, ["regionLabel", "region_label", "sampleRegionLabel"])}</dd>
                </div>
                <div>
                  <dt>기간</dt>
                  <dd>
                    {formatDate(firstRecordValue(campaign, ["startsAt", "starts_at"]))} -
                    {formatDate(firstRecordValue(campaign, ["endsAt", "ends_at"]))}
                  </dd>
                </div>
              </dl>
              <div className="card-actions">
                {["active", "paused", "completed", "archived", "rejected"].map((nextStatus) => (
                  <button
                    className="chip-button"
                    disabled={busyId === campaign.id || status === nextStatus || !campaign.id}
                    key={nextStatus}
                    onClick={() => void onStatusChange(campaign, nextStatus)}
                    type="button"
                  >
                    {getCampaignStatusLabel(nextStatus)}
                  </button>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RecordWorkspace({
  columns,
  description,
  icon,
  records,
  title,
}: {
  columns: Array<[string, string[]]>;
  description: string;
  icon: ReactNode;
  records: AdminRecord[];
  title: string;
}) {
  return (
    <section className="panel workspace-panel">
      <div className="section-heading">
        <div className="heading-icon">{icon}</div>
        <div>
          <p className="eyebrow">Management</p>
          <h2>
            {title} {records.length}건
          </h2>
          <p>{description}</p>
        </div>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {columns.map(([label]) => (
                <th key={label}>{label}</th>
              ))}
              <th>ID</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record, index) => (
              <tr key={record.id || index}>
                {columns.map(([label, keys]) => (
                  <td key={label}>{firstRecordText(record, keys)}</td>
                ))}
                <td>{record.id || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CommerceWorkspace({
  claims,
  orders,
}: {
  claims: AdminRecord[];
  orders: AdminRecord[];
}) {
  return (
    <div className="split-workspace">
      <RecordWorkspace
        description="최근 주문 흐름을 확인합니다."
        icon={<ShoppingCart size={20} />}
        records={orders}
        title="주문"
        columns={[
          ["주문번호", ["orderNumber", "order_number", "id"]],
          ["상품", ["productName", "product_name"]],
          ["수령자", ["recipientNameSnapshot", "recipient_name_snapshot"]],
          ["상태", ["orderStatus", "order_status", "status"]],
        ]}
      />
      <RecordWorkspace
        description="교환, 환불, 배송 클레임을 확인합니다."
        icon={<AlertTriangle size={20} />}
        records={claims}
        title="클레임"
        columns={[
          ["주문번호", ["orderNumber", "order_number", "orderId"]],
          ["유형", ["claimType", "claim_type", "type"]],
          ["상태", ["claimStatus", "claim_status", "status"]],
          ["요청일", ["createdAt", "created_at"]],
        ]}
      />
    </div>
  );
}

function MetricCard({
  icon,
  label,
  tone,
  value,
}: {
  icon: ReactNode;
  label: string;
  tone: string;
  value: number;
}) {
  return (
    <div className={`metric-card tone-${tone}`}>
      <span>{icon}</span>
      <div>
        <strong>{value.toLocaleString("ko-KR")}</strong>
        <small>{label}</small>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="detail-row">
      <span>{label}</span>
      <strong>{text(value)}</strong>
    </div>
  );
}

function filterRequests(requests: SampleRequest[], filters: DeliveryFilters) {
  const keyword = filters.keyword.trim().toLowerCase();
  const region = filters.region.trim().toLowerCase();

  return requests.filter((request) => {
    if (filters.campaignId && request.sampleCampaignId !== filters.campaignId) return false;
    if (filters.rider === "unassigned" && request.riderUserId) return false;
    if (filters.rider && filters.rider !== "unassigned" && request.riderUserId !== filters.rider) {
      return false;
    }
    if (!matchesStatusFilter(request, filters.status)) return false;
    if (region && !regionLabel(request).toLowerCase().includes(region)) return false;
    if (!keyword) return true;
    return [
      request.productName,
      request.brandName,
      request.sampleCampaignTitle,
      request.recipientNameSnapshot,
      request.deliveryPhoneMaskedSnapshot,
      request.contactPhoneMaskedSnapshot,
      request.addressSnapshot,
      request.addressDetailSnapshot,
      request.riderName,
      request.riderCi,
      regionLabel(request),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(keyword);
  });
}

function matchesStatusFilter(request: SampleRequest, status: string) {
  if (status === "all") return true;
  if (status === "preparing_group") return PREPARING_STATUSES.has(request.status);
  if (status === "delivered_group") return DONE_STATUSES.has(request.status);
  if (status === "issue_group") return getRequestStatusMeta(request.status).group === "issue";
  if (status === "payout_pending") {
    return (request.payoutStatus ?? "pending") === "pending" && DONE_STATUSES.has(request.status);
  }
  return request.status === status;
}

function buildDeliveryStats(requests: SampleRequest[]) {
  return requests.reduce(
    (acc, request) => {
      const meta = getRequestStatusMeta(request.status);
      if (request.status === "requested") acc.requested += 1;
      if (PREPARING_STATUSES.has(request.status)) acc.preparing += 1;
      if (request.status === "shipping") acc.shipping += 1;
      if (DONE_STATUSES.has(request.status)) acc.done += 1;
      if (meta.group === "issue") acc.issue += 1;
      if ((request.payoutStatus ?? "pending") === "pending" && DONE_STATUSES.has(request.status)) {
        acc.payoutPending += 1;
      }
      return acc;
    },
    { requested: 0, preparing: 0, shipping: 0, done: 0, issue: 0, payoutPending: 0 },
  );
}

function requestLabel(request: SampleRequest) {
  return request.productName || request.sampleCampaignTitle || request.id;
}

function canAssignRider(request: SampleRequest) {
  return ["requested", "preparing", "rider_assigned"].includes(request.status);
}

function riderLabel(request: SampleRequest) {
  return [request.riderName, request.riderCi].filter(Boolean).join(" / ") || "미배정";
}

function regionLabel(request: SampleRequest) {
  return [request.sidoNameSnapshot, request.sigunguNameSnapshot, request.bnameSnapshot]
    .filter(Boolean)
    .join(" ") || request.sampleRegionLabel || "-";
}

function fullAddress(request: SampleRequest) {
  return [request.addressSnapshot, request.addressDetailSnapshot].filter(Boolean).join(" ") || "-";
}

function locationLabel(request: SampleRequest) {
  if (!request.deliveryLocationLat || !request.deliveryLocationLng) return "-";
  return `${request.deliveryLocationLat}, ${request.deliveryLocationLng}`;
}

function formatDate(value: unknown) {
  const source = text(value, "");
  if (!source) return "-";
  const date = new Date(source);
  if (Number.isNaN(date.getTime())) return source;
  return new Intl.DateTimeFormat("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function text(value: unknown, fallback = "-") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value.trim() || fallback;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : fallback;
  if (typeof value === "boolean") return value ? "예" : "아니오";
  return fallback;
}

function firstRecordValue(record: AdminRecord, keys: string[]) {
  for (const key of keys) {
    if (record[key] !== null && record[key] !== undefined && record[key] !== "") {
      return record[key];
    }
  }
  return undefined;
}

function firstRecordText(record: AdminRecord, keys: string[], fallback = "-") {
  return text(firstRecordValue(record, keys), fallback);
}

function recordTitle(record: AdminRecord) {
  return firstRecordText(record, ["title", "name", "companyName", "company_name", "id"], "제목 없음");
}
